"use client";

import React, { useState, useEffect } from "react";
import { Badge } from "@/components/ui/feedback";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import {
  Label,
  Input,
  Select,
  ErrorMessage,
} from "@/components/ui/form-elements";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/overlays";
import {
  createAssetAction,
  updateAssetAction,
  deleteAssetAction,
} from "@/actions/ops-actions";
import { useRouter } from "next/navigation";
import Link from "next/link";
import {
  Search,
  Plus,
  Trash2,
  Edit2,
  Cpu,
  Key,
  Database,
  Eye,
  EyeOff,
  User,
  MapPin,
  FileText,
  TrendingUp,
} from "lucide-react";

interface AgentItem {
  id: string;
  name: string;
}

interface AssetItem {
  id: string;
  name: string;
  assetType: "equipment" | "resource" | "credential";
  status: "available" | "in_use" | "expired" | "active";
  notes: string;
  metadata: {
    serialNumber?: string;
    location?: string;
    url?: string;
    username?: string;
    password?: string;
    ownerId?: string;
    capacity?: string;
  };
  createdAt: string;
  updatedAt: string;
}

interface AssetsClientProps {
  initialAssets: AssetItem[];
  agents: AgentItem[];
}

export function AssetsClient({ initialAssets, agents }: AssetsClientProps) {
  const router = useRouter();
  const [assetsList, setAssetsList] = useState<AssetItem[]>(initialAssets);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAsset, setEditingAsset] = useState<AssetItem | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Visibility map for password masking
  const [visiblePasswords, setVisiblePasswords] = useState<
    Record<string, boolean>
  >({});

  // Form State
  const [name, setName] = useState("");
  const [assetType, setAssetType] = useState<
    "equipment" | "resource" | "credential"
  >("equipment");
  const [status, setStatus] = useState<
    "available" | "in_use" | "expired" | "active"
  >("available");
  const [notes, setNotes] = useState("");

  // Type-specific form fields
  const [serialNumber, setSerialNumber] = useState("");
  const [location, setLocation] = useState("");
  const [url, setUrl] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [ownerId, setOwnerId] = useState("");
  const [capacity, setCapacity] = useState("");

  useEffect(() => {
    setAssetsList(initialAssets);
  }, [initialAssets]);

  const togglePasswordVisibility = (id: string) => {
    setVisiblePasswords((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const handleOpenAddModal = () => {
    setEditingAsset(null);
    setName("");
    setAssetType("equipment");
    setStatus("available");
    setNotes("");
    setSerialNumber("");
    setLocation("");
    setUrl("");
    setUsername("");
    setPassword("");
    setOwnerId("");
    setCapacity("");
    setError("");
    setIsModalOpen(true);
  };

  const handleOpenEditModal = (asset: AssetItem) => {
    setEditingAsset(asset);
    setName(asset.name);
    setAssetType(asset.assetType);
    setStatus(asset.status);
    setNotes(asset.notes);
    setSerialNumber(asset.metadata.serialNumber || "");
    setLocation(asset.metadata.location || "");
    setUrl(asset.metadata.url || "");
    setUsername(asset.metadata.username || "");
    setPassword(asset.metadata.password || "");
    setOwnerId(asset.metadata.ownerId || "");
    setCapacity(asset.metadata.capacity || "");
    setError("");
    setIsModalOpen(true);
  };

  // Keep status synchronized when type changes
  const handleTypeChange = (
    newType: "equipment" | "resource" | "credential",
  ) => {
    setAssetType(newType);
    if (newType === "credential") {
      setStatus("active");
    } else {
      setStatus("available");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!name.trim()) {
      setError("Il nome dell'asset è obbligatorio.");
      return;
    }

    setLoading(true);

    const metadata: Record<string, any> = {};
    if (assetType === "equipment") {
      metadata.serialNumber = serialNumber;
      metadata.location = location;
      metadata.ownerId = ownerId;
    } else if (assetType === "resource") {
      metadata.location = location;
      metadata.capacity = capacity;
      metadata.ownerId = ownerId;
    } else if (assetType === "credential") {
      metadata.url = url;
      metadata.username = username;
      metadata.password = password;
    }

    try {
      if (editingAsset) {
        // Edit flow
        const res = await updateAssetAction({
          id: editingAsset.id,
          name,
          assetType,
          status,
          notes,
          metadata,
        });

        if (res.error) {
          setError(res.error);
        } else {
          setIsModalOpen(false);
          router.refresh();
        }
      } else {
        // Create flow
        const res = await createAssetAction({
          name,
          assetType,
          status,
          notes,
          metadata,
        });

        if (res.error) {
          setError(res.error);
        } else {
          setIsModalOpen(false);
          router.refresh();
        }
      }
    } catch (err: any) {
      setError(
        err.message || "Si è verificato un errore durante il salvataggio.",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Sei sicuro di voler eliminare questo asset?")) return;

    try {
      const res = await deleteAssetAction(id);
      if (res.error) {
        alert(res.error);
      } else {
        router.refresh();
      }
    } catch (err) {
      alert("Errore durante l'eliminazione.");
    }
  };

  const filteredAssets = assetsList.filter((asset) => {
    const matchesSearch =
      asset.name.toLowerCase().includes(search.toLowerCase()) ||
      asset.notes.toLowerCase().includes(search.toLowerCase()) ||
      (asset.metadata.location &&
        asset.metadata.location.toLowerCase().includes(search.toLowerCase()));

    const matchesType = typeFilter === "all" || asset.assetType === typeFilter;

    return matchesSearch && matchesType;
  });

  const getAssetIcon = (type: AssetItem["assetType"]) => {
    switch (type) {
      case "equipment":
        return <Cpu className="h-5 w-5 text-primary-500" />;
      case "resource":
        return <Database className="h-5 w-5 text-success" />;
      case "credential":
        return <Key className="h-5 w-5 text-warning" />;
    }
  };

  const getAssetTypeLabel = (type: AssetItem["assetType"]) => {
    switch (type) {
      case "equipment":
        return "Hardware";
      case "resource":
        return "Risorsa Fis./Log.";
      case "credential":
        return "Credenziale";
    }
  };

  const getStatusBadge = (status: AssetItem["status"]) => {
    switch (status) {
      case "available":
        return <Badge variant="success">Disponibile</Badge>;
      case "in_use":
        return <Badge variant="warning">In Uso</Badge>;
      case "active":
        return <Badge variant="success">Attivo</Badge>;
      case "expired":
        return <Badge variant="error">Scaduto</Badge>;
    }
  };

  const getOwnerName = (id?: string) => {
    if (!id) return "Non assegnato";
    const agent = agents.find((a) => a.id === id);
    return agent ? agent.name : "Nessuno";
  };

  return (
    <div className="space-y-6">
      {/* Search & Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-center justify-between bg-card p-4 rounded-xl border border-border">
        <div className="relative w-full sm:max-w-xs">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-text-muted" />
          <Input
            placeholder="Cerca asset per nome, note..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="flex w-full sm:w-auto gap-3 items-center justify-between sm:justify-end">
          <Select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="w-[170px]"
          >
            <option value="all">Tutti i tipi</option>
            <option value="equipment">Hardware (Equipment)</option>
            <option value="resource">Spazi & Risorse (Resource)</option>
            <option value="credential">Credenziali (Credential)</option>
          </Select>

          <Button onClick={handleOpenAddModal} className="gap-1.5 shrink-0">
            <Plus className="h-4 w-4" />
            Nuovo Asset
          </Button>
        </div>
      </div>

      {/* Grid of Assets */}
      {filteredAssets.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 border border-dashed border-border rounded-2xl bg-card/50 text-center max-w-xl mx-auto my-8">
          <Cpu className="h-10 w-10 text-primary-500 mb-3" />
          <h3 className="text-base font-bold text-text-primary">
            Nessun asset trovato
          </h3>
          <p className="text-sm text-text-secondary mt-1">
            Non ci sono asset o risorse corrispondenti ai filtri selezionati.
            Crea un nuovo asset per cominciare.
          </p>
          <Button onClick={handleOpenAddModal} className="mt-4 gap-1.5">
            <Plus className="h-4 w-4" />
            Crea Primo Asset
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAssets.map((asset) => (
            <Card
              key={asset.id}
              className="relative overflow-hidden flex flex-col justify-between hover:border-primary-500/50 transition-all border-l-4 border-l-primary-500 group cursor-pointer"
              onClick={() => handleOpenEditModal(asset)}
            >
              {/* Hover Actions */}
              <div
                className="absolute top-3 right-3 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10"
                onClick={(e) => e.stopPropagation()}
              >
                <Link
                  href={`/explore?nodeId=${asset.id}`}
                  title="Esplora relazioni"
                >
                  <Button
                    variant="secondary"
                    size="icon"
                    className="h-7 w-7 p-0 flex items-center justify-center"
                  >
                    <TrendingUp className="h-3.5 w-3.5 text-text-secondary hover:text-primary-600" />
                  </Button>
                </Link>
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => handleOpenEditModal(asset)}
                  title="Modifica"
                  className="h-7 w-7 p-0 flex items-center justify-center"
                >
                  <Edit2 className="h-3.5 w-3.5 text-text-secondary hover:text-primary-600" />
                </Button>
                <Button
                  variant="secondary"
                  size="icon"
                  onClick={() => handleDelete(asset.id)}
                  title="Elimina"
                  className="h-7 w-7 p-0 flex items-center justify-center hover:bg-error-soft hover:text-error"
                >
                  <Trash2 className="h-3.5 w-3.5 text-text-secondary" />
                </Button>
              </div>

              <CardHeader className="pb-2 pr-24">
                <div className="flex items-start gap-2">
                  {getAssetIcon(asset.assetType)}
                  <div>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] font-semibold text-text-muted uppercase tracking-wider block">
                        {getAssetTypeLabel(asset.assetType)}
                      </span>
                      <div
                        onClick={(e) => e.stopPropagation()}
                        className="scale-90 origin-left"
                      >
                        {getStatusBadge(asset.status)}
                      </div>
                    </div>
                    <h3 className="font-bold text-sm text-text-primary leading-snug">
                      <Link
                        href={`/explore?nodeId=${asset.id}`}
                        className="hover:text-primary-600 hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {asset.name}
                      </Link>
                    </h3>
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-3 pb-3 flex-1 text-xs">
                {/* Equipment fields */}
                {asset.assetType === "equipment" && (
                  <div className="space-y-1.5 text-text-secondary">
                    {asset.metadata.serialNumber && (
                      <div>
                        S/N:{" "}
                        <strong className="text-text-primary">
                          {asset.metadata.serialNumber}
                        </strong>
                      </div>
                    )}
                    {asset.metadata.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5 text-text-muted" />
                        <span>
                          Posizione: <strong>{asset.metadata.location}</strong>
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <User className="h-3.5 w-3.5 text-text-muted" />
                      <span>
                        Assegnato a:{" "}
                        <strong>{getOwnerName(asset.metadata.ownerId)}</strong>
                      </span>
                    </div>
                  </div>
                )}

                {/* Resource fields */}
                {asset.assetType === "resource" && (
                  <div className="space-y-1.5 text-text-secondary">
                    {asset.metadata.capacity && (
                      <div>
                        Capacità/Dettaglio:{" "}
                        <strong className="text-text-primary">
                          {asset.metadata.capacity}
                        </strong>
                      </div>
                    )}
                    {asset.metadata.location && (
                      <div className="flex items-center gap-1">
                        <MapPin className="h-3.5 w-3.5 text-text-muted" />
                        <span>
                          Posizione: <strong>{asset.metadata.location}</strong>
                        </span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <User className="h-3.5 w-3.5 text-text-muted" />
                      <span>
                        Responsabile:{" "}
                        <strong>{getOwnerName(asset.metadata.ownerId)}</strong>
                      </span>
                    </div>
                  </div>
                )}

                {/* Credential fields */}
                {asset.assetType === "credential" && (
                  <div
                    className="space-y-2 bg-card-muted/50 p-2.5 rounded-lg border border-border/40 font-mono"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {asset.metadata.url && (
                      <div className="truncate">
                        URL:{" "}
                        <a
                          href={asset.metadata.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {asset.metadata.url}
                        </a>
                      </div>
                    )}
                    {asset.metadata.username && (
                      <div className="truncate">
                        User:{" "}
                        <strong className="text-text-primary">
                          {asset.metadata.username}
                        </strong>
                      </div>
                    )}
                    {asset.metadata.password && (
                      <div className="flex items-center justify-between gap-2 mt-1">
                        <span className="truncate">
                          Pass:{" "}
                          <strong className="text-text-primary">
                            {visiblePasswords[asset.id]
                              ? asset.metadata.password
                              : "••••••••••••"}
                          </strong>
                        </span>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePasswordVisibility(asset.id);
                          }}
                          className="text-text-muted hover:text-text-primary outline-none"
                        >
                          {visiblePasswords[asset.id] ? (
                            <EyeOff className="h-3.5 w-3.5" />
                          ) : (
                            <Eye className="h-3.5 w-3.5" />
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                {/* General Notes */}
                {asset.notes && (
                  <div className="text-xs text-text-secondary bg-card-muted/30 p-2 rounded-lg border border-border/20 flex items-start gap-1">
                    <FileText className="h-3.5 w-3.5 text-text-muted shrink-0 mt-0.5" />
                    <p className="line-clamp-2">{asset.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingAsset ? "Modifica Asset" : "Aggiungi Nuovo Asset"}
            </DialogTitle>
            <DialogDescription>
              Inserisci o aggiorna le informazioni dell'equipaggiamento,
              credenziale di sistema o risorsa.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 my-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="asset-name">Nome Asset</Label>
                <Input
                  id="asset-name"
                  placeholder="es. MacBook Pro di Luca"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={loading}
                  required
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="asset-type">Tipo di Asset</Label>
                <Select
                  id="asset-type"
                  value={assetType}
                  onChange={(e) => handleTypeChange(e.target.value as any)}
                  disabled={loading}
                >
                  <option value="equipment">Hardware (Equipment)</option>
                  <option value="resource">Spazio & Risorsa (Resource)</option>
                  <option value="credential">
                    Credenziale / Accesso (Credential)
                  </option>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="asset-status">Stato</Label>
                <Select
                  id="asset-status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value as any)}
                  disabled={loading}
                >
                  {assetType === "credential" ? (
                    <>
                      <option value="active">Attiva</option>
                      <option value="expired">Scaduta</option>
                    </>
                  ) : (
                    <>
                      <option value="available">Disponibile</option>
                      <option value="in_use">In Uso</option>
                      <option value="expired">Scaduto / Fuori uso</option>
                    </>
                  )}
                </Select>
              </div>

              {/* Owner assignment (for equipment & resource only) */}
              {assetType !== "credential" && (
                <div className="space-y-1.5">
                  <Label htmlFor="asset-owner">
                    Assegnato a / Responsabile
                  </Label>
                  <Select
                    id="asset-owner"
                    value={ownerId}
                    onChange={(e) => setOwnerId(e.target.value)}
                    disabled={loading}
                  >
                    <option value="">Non assegnato</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name}
                      </option>
                    ))}
                  </Select>
                </div>
              )}
            </div>

            {/* Type-Specific Fields */}
            {assetType === "equipment" && (
              <div className="grid grid-cols-2 gap-4 border-t border-border/40 pt-3">
                <div className="space-y-1.5">
                  <Label htmlFor="eq-serial">Numero di Serie (S/N)</Label>
                  <Input
                    id="eq-serial"
                    placeholder="es. C02XN123LVD"
                    value={serialNumber}
                    onChange={(e) => setSerialNumber(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="eq-location">Posizione / Ufficio</Label>
                  <Input
                    id="eq-location"
                    placeholder="es. Sede Milano, Scrivania 4"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            {assetType === "resource" && (
              <div className="grid grid-cols-2 gap-4 border-t border-border/40 pt-3">
                <div className="space-y-1.5">
                  <Label htmlFor="res-capacity">Capacità / Dettagli</Label>
                  <Input
                    id="res-capacity"
                    placeholder="es. 10 Persone / 100 GB"
                    value={capacity}
                    onChange={(e) => setCapacity(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="res-location">Ubicazione</Label>
                  <Input
                    id="res-location"
                    placeholder="es. Piano 2, Ufficio Tecnico"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    disabled={loading}
                  />
                </div>
              </div>
            )}

            {assetType === "credential" && (
              <div className="space-y-3 border-t border-border/40 pt-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cred-url">URL del Servizio</Label>
                  <Input
                    id="cred-url"
                    placeholder="es. https://github.com"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    disabled={loading}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="cred-username">
                      Username / Email di Accesso
                    </Label>
                    <Input
                      id="cred-username"
                      placeholder="es. admin@cleos.io"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      disabled={loading}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="cred-password">
                      Password / Token di Accesso
                    </Label>
                    <Input
                      id="cred-password"
                      type="text"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      disabled={loading}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-1.5 border-t border-border/40 pt-3">
              <Label htmlFor="asset-notes">
                Note ed Informazioni Aggiuntive (Opzionale)
              </Label>
              <textarea
                id="asset-notes"
                placeholder="Specifiche tecniche, scadenze contrattuali, link alle policy, ecc..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={loading}
                rows={3}
                className="flex w-full rounded-lg border border-border bg-card px-3 py-2 text-sm shadow-sm transition-colors placeholder:text-text-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:border-primary-500 disabled:cursor-not-allowed disabled:opacity-50 text-foreground resize-none"
              />
            </div>

            {error && <ErrorMessage>{error}</ErrorMessage>}

            <div className="flex justify-end gap-2 pt-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setIsModalOpen(false)}
                disabled={loading}
              >
                Annulla
              </Button>
              <Button type="submit" loading={loading}>
                {editingAsset ? "Salva Modifiche" : "Aggiungi Asset"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
